/**
 * Deutsche Beschreibung einer Wiederholungsregel.
 *
 * Bewusst eigenhaendig statt ueber `rrule.toText()`: dessen Uebersetzungen
 * sind unvollstaendig und klingen an mehreren Stellen unnatuerlich
 * ("jeden 1 Woche"). Abgedeckt wird genau das, was die Oberflaeche erzeugen
 * kann - fuer alles andere gibt es eine ehrliche Rueckfallformulierung.
 */
import { formatDate } from '@/lib/datetime';

const WEEKDAYS: Record<string, string> = {
  MO: 'Montag', TU: 'Dienstag', WE: 'Mittwoch', TH: 'Donnerstag',
  FR: 'Freitag', SA: 'Samstag', SU: 'Sonntag',
};

const ORDINALS = ['', 'zweiten', 'dritten', 'vierten', 'fünften', 'sechsten'];

function parts(rule: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of rule.replace(/^RRULE:/i, '').split(';')) {
    const [key, value] = part.split('=');
    if (key && value) out[key.toUpperCase()] = value;
  }
  return out;
}

/** Verbindet mit Komma und abschliessendem "und". */
function joinDe(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} und ${items[items.length - 1]}`;
}

export function describeRrule(rule: string | undefined): string {
  if (!rule) return 'Wiederholt sich nicht';

  const p = parts(rule);
  const interval = Math.max(1, +(p.INTERVAL ?? '1'));
  let text: string;

  switch (p.FREQ) {
    case 'DAILY':
      text = interval === 1 ? 'Täglich' : `Alle ${interval} Tage`;
      break;

    case 'WEEKLY': {
      const days = (p.BYDAY ?? '').split(',').filter(Boolean).map((d) => WEEKDAYS[d] ?? d);
      const rhythm = interval === 1
        ? 'Wöchentlich'
        : `Alle ${interval} Wochen`;
      text = days.length > 0 ? `${rhythm} am ${joinDe(days)}` : rhythm;
      break;
    }

    case 'MONTHLY': {
      const rhythm = interval === 1 ? 'Monatlich' : `Alle ${interval} Monate`;
      if (p.BYDAY) {
        // z. B. BYDAY=2TU -> "am zweiten Dienstag"
        const match = /^(-?\d)([A-Z]{2})$/.exec(p.BYDAY);
        if (match) {
          const n = +match[1]!;
          const day = WEEKDAYS[match[2]!] ?? match[2]!;
          const ordinal = n === -1 ? 'letzten' : n === 1 ? 'ersten' : (ORDINALS[n - 1] ?? `${n}.`);
          text = `${rhythm} am ${ordinal} ${day}`;
          break;
        }
      }
      text = p.BYMONTHDAY ? `${rhythm} am ${p.BYMONTHDAY}.` : rhythm;
      break;
    }

    case 'YEARLY':
      text = interval === 1 ? 'Jährlich' : `Alle ${interval} Jahre`;
      break;

    default:
      return 'Eigene Wiederholungsregel';
  }

  if (p.COUNT) {
    text += `, ${p.COUNT}-mal`;
  } else if (p.UNTIL) {
    const until = p.UNTIL;
    const date = `${until.slice(0, 4)}-${until.slice(4, 6)}-${until.slice(6, 8)}`;
    text += `, bis ${formatDate(date)}`;
  }

  return text;
}
